import { useRef, useState } from 'react';
import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { applyAuthenticatedSession } from 'lib/auth';
import { Alert, Button } from 'components/common';
import { Form, FormGroup, Text } from 'components/common/form';

interface Props {
  isOpen: boolean;
  next?: string;
  onOpenClick?: (strategy: string) => void;
  onSuccess?: () => void;
  registration?: boolean;
  sessionUserEmail?: string;
  strategy: any;
}

const AuthStrategyFormLogin = ({
  isOpen,
  next,
  onOpenClick,
  onSuccess,
  registration = false,
  sessionUserEmail,
  strategy
}: Props) => {
  const inputContainer = useRef<HTMLDivElement>(undefined);

  const { dispatch } = useDispatch();
  const {
    language,
    messages,
    pageState: { user },
    theme
  } = useReduxState();

  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleOpen = () => {
    onOpenClick(strategy.name);
  };

  const handleSubmit = async (values) => {
    try {
      setError(null);
      setIsLoading(true);

      const response = await api.post(`/auth/${strategy.name}/authenticate`, {
        email: sessionUserEmail || values.email,
        password: values.password
      });

      if (response.data?.success) {
        await applyAuthenticatedSession({
          dispatch,
          user: response.data.user
        });
        if (onSuccess) {
          onSuccess();
        } else {
          window.location.href = next || '/';
        }
      } else {
        let error = messages.commonError;
        if (response.data?.error === 'user_not_found') {
          error = messages.authSignInError;
        } else if (response.data?.error === 'account_expired') {
          error = messages.authAccountExpired;
        } else if (response.data?.error === 'account_locked') {
          error = messages.authAccountLocked;
        }
        setIsLoading(false);
        setError(error);
      }
    } catch (error) {
      setIsLoading(false);
      setError(messages.commonError);
    }
  };

  if (!isOpen) {
    return (
      <Button
        customBackgroundColor={strategy.buttonColor || theme.mainColor}
        customBackgroundColorHover={
          strategy.buttonHoverColor || theme.mainColorActive
        }
        customColor={strategy.buttonTextColor || theme.mainTextColor}
        onClick={handleOpen}
        style={{ marginTop: 15, width: '100%' }}
        text={
          registration
            ? strategy.buttonTextRegister || strategy.name
            : strategy.buttonText || strategy.name
        }
      />
    );
  }

  return (
    <div className="login-form">
      <Form onSubmit={handleSubmit}>
        {!sessionUserEmail ? (
          <FormGroup htmlFor="email" label={messages.commonEmail}>
            <Text
              disabled={registration && user?.email}
              field="email"
              id="email"
              initialValue={registration && user?.email ? user.email : ''}
              inputContainerRef={(el) => (inputContainer.current = el)}
              placeholder={messages.loginEmailPlaceholder}
              required={true}
              type="text"
              validateOnChange={true}
            />
          </FormGroup>
        ) : null}
        <FormGroup htmlFor="password" label={messages.commonPassword}>
          <Text
            field="password"
            id="password"
            required={true}
            type="password"
            validateOnChange={true}
          />
        </FormGroup>

        {error ? (
          <Alert
            className="login-error"
            style={{ marginTop: 20 }}
            text={error}
            type="error-light"
          />
        ) : null}

        <Button
          customBackgroundColor={strategy.buttonColor || theme.mainColor}
          customBackgroundColorHover={
            strategy.buttonHoverColor || theme.mainColorActive
          }
          customColor={strategy.buttonTextColor || theme.mainTextColor}
          loading={isLoading}
          text={messages.authSignIn}
          style={{ marginTop: 20, width: '100%' }}
          type="submit"
        />
      </Form>
    </div>
  );
};

export default AuthStrategyFormLogin;
